import { PlayerRef } from '@remotion/player';
import React, { useCallback, useEffect } from 'react';
import { addAsset } from '../assets/add-asset';
import {
  copyEditorSelection,
  pasteEditorClipboard,
} from '@waka/compat/clipboard-actions';
import { parseItemsFromClipboardTextHtml } from '../clipboard/parse-items';
import {
  FEATURE_COPY_LAYERS,
  FEATURE_CUT_LAYERS,
  FEATURE_PASTE_ASSETS,
  FEATURE_PASTE_TEXT,
} from '../flags';
import { createTextItem } from '../items/text/create-text-item';
import { addItem } from '../state/actions/add-item';
import { isEventTargetInputElement } from '../utils/is-event-target-input-element';
import {
  useCurrentStateAsRef,
  useTracks,
  useWriteContext,
} from '../utils/use-context';

export const CopyPasteLayers: React.FC<{
  playerRef: React.RefObject<PlayerRef | null>;
}> = ({ playerRef }) => {
  const timelineWriteContext = useWriteContext();
  const { setState } = timelineWriteContext;
  const state = useCurrentStateAsRef();
  const { tracks } = useTracks();

  const performCopy = useCallback(
    async (e: ClipboardEvent) => {
      if (!e.clipboardData) {
        return;
      }

      const { selectedItems } = state.current;

      if (selectedItems.length === 0) {
        return;
      }

      await copyEditorSelection({
        stateRef: state,
        setState,
        ids: selectedItems,
        event: e,
      });
    },
    [state, setState],
  );

  const handleCopy = useCallback(
    async (e: ClipboardEvent) => {
      // input element들에서의 기본 copy와 간섭하지 않도록 하기
      if (isEventTargetInputElement(e)) {
        return;
      }

      await performCopy(e);
    },
    [performCopy],
  );

  const handleCut = useCallback(
    async (e: ClipboardEvent) => {
      // input element들에서의 기본 cut과 간섭하지 않도록 하기
      if (isEventTargetInputElement(e)) {
        return;
      }

      const { selectedItems } = state.current;
      if (selectedItems.length === 0) {
        return;
      }

      // lock된 아이템 필터링
      const cuttableItems = selectedItems.filter((itemId) => {
        const track = tracks.find((t) => t.items.includes(itemId));
        return !track?.locked;
      });

      if (cuttableItems.length === 0) {
        return;
      }

      await copyEditorSelection({
        stateRef: state,
        setState,
        ids: cuttableItems,
        cut: true,
        event: e,
      });
    },
    [performCopy, setState, state, tracks],
  );

  const handlePaste = useCallback(
    async (e: ClipboardEvent) => {
      // form field 내에서의 paste 재정의를 피하기
      if (isEventTargetInputElement(e)) {
        return;
      }
      if (!e.clipboardData) {
        return;
      }

      e.preventDefault();

      if (e.clipboardData.types.includes('text/html')) {
        const text = e.clipboardData.getData('text/html');
        const parsedAsItems = parseItemsFromClipboardTextHtml(text);
        if (parsedAsItems) {
          pasteEditorClipboard({
            stateRef: state,
            setState,
            payload: parsedAsItems,
            from: playerRef.current?.getCurrentFrame() ?? 0,
          });
          return;
        }
      }
      const text = e.clipboardData.getData('text/plain');

      const portable = parseItemsFromClipboardTextHtml(text);
      if (portable) {
        pasteEditorClipboard({
          stateRef: state,
          setState,
          payload: portable,
          from: playerRef.current?.getCurrentFrame() ?? 0,
        });
        return;
      }

      if (FEATURE_PASTE_TEXT && text.trim() !== '') {
        const { compositionWidth, compositionHeight } =
          state.current.undoableState;
        const item = await createTextItem({
          xOnCanvas: compositionWidth / 2,
          yOnCanvas: compositionHeight / 2,
          from: playerRef.current?.getCurrentFrame() ?? 0,
          text,
          align: 'center',
        });

        setState({
          update: (prev) =>
            addItem({
              state: prev,
              item,
              select: true,
              position: { type: 'front' },
            }),
          commitToUndoStack: true,
        });
        return;
      }

      const fps = state.current.undoableState.fps;
      const tracks = state.current.undoableState.tracks;

      const hasFiles = e.clipboardData.types.includes('Files');
      if (hasFiles && FEATURE_PASTE_ASSETS) {
        const { compositionHeight, compositionWidth } =
          state.current.undoableState;
        for (const file of e.clipboardData.files) {
          await addAsset({
            file,
            compositionHeight,
            compositionWidth,
            fps,
            tracks,
            timelineWriteContext: timelineWriteContext,
            playerRef,
            dropPosition: null,
            filename: file.name,
          });
        }
        return;
      }
    },
    [playerRef, setState, timelineWriteContext, state],
  );

  useEffect(() => {
    if (!FEATURE_COPY_LAYERS) {
      return;
    }

    document.addEventListener('copy', handleCopy);
    document.addEventListener('paste', handlePaste);

    return () => {
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('paste', handlePaste);
    };
  }, [handleCopy, handleCut, handlePaste]);

  useEffect(() => {
    if (!FEATURE_CUT_LAYERS) {
      return;
    }

    document.addEventListener('cut', handleCut);

    return () => {
      document.removeEventListener('cut', handleCut);
    };
  }, [handleCut]);

  return null;
};
